import { z } from 'zod';
import { titleCase } from '../titleCase.ts';
import type { ProcessStep, ProjectProcess } from './graph.ts';
import { TASK_PHASE_RULE, phaseOfNode } from './phases.ts';
import { schedule, type Schedule } from './schedule.ts';
import { DECISION_CHOOSE, NODE_SOURCES, type NodeKind, type ProcessNodeDefT, type ProcessTreeDoc } from './schema.ts';
import { SPECIAL_KEYS, SPECIAL_TASKS, specialsOf, type SpecialKey } from './specials.ts';

/**
 * The tree builder (Jason, 2026-10-04: "make the tools for building out the
 * tree myself. so all the nodes in a pane, the ability to create new nodes,
 * and add comments on the nodes, and link depends on. this should be a
 * building tool in dev that we will work on a bit before locking things in").
 * Drawn first in design study 03 (docs/design/studies), revision 2 and the
 * changes Jason asked of it.
 *
 * It starts fresh from the master tree and joins nothing to anything ("you
 * don't understand that this tree builder should have no relationships between
 * nodes, phases, gates, decisions"): each node brings its title, kind, who does
 * it, its trade, its task list and its picture, and Jason draws every
 * dependency, phase and sign-off himself. A node is a task, a decision (with
 * options, one of them chosen or any, which a job records as it is decided;
 * never a branch: design study 22), a
 * sign-off (a decision both NSB and the client sign, with one way on) or a
 * milestone. Its time is a range, blank until said. Until everything a node of
 * its kind needs is said it is unconfigured: drawn grey, without its picture.
 *
 * What the tree is for, said plainly (Jason, 2026-10-04): building it is a
 * job's discovery and planning, and changing it is refining the work. It makes
 * two things: the work (each task, and who does it) and the funnel (when each
 * phase ends, and how sure the price is by then), drawn from the board as it
 * is built, along one way through each decision.
 */

/** What a node can be made: a task, a decision, a sign-off or a milestone. */
export const BUILDER_KINDS = ['task', 'decision', 'gate', 'milestone'] as const;
/**
 * The two ends of the process (Jason, 2026-10-04: "we need to have a start and
 * end node, special nodes like project setup and project archive, truly the
 * start and end of the process"): one of each, on the board from the first,
 * never deleted, taken off it or put in a phase; nothing comes before the
 * start, nothing after the end.
 */
export const BUILDER_ENDS = ['start', 'end'] as const;
export type BuilderEnd = (typeof BUILDER_ENDS)[number];
export type BuilderKind = (typeof BUILDER_KINDS)[number] | BuilderEnd;
const ALL_KINDS = [...BUILDER_KINDS, ...BUILDER_ENDS] as const;
/** A gate is a sign-off: a decision two parties sign, NSB and the client (Jason, 2026-10-04). */
/** A gate has no word: it is its mark alone (Jason, 2026-10-06: "just don't have anything for gates, maybe just a gate icon"). */
export const BUILDER_KIND_WORDS: Record<BuilderKind, string> = { task: 'Work', decision: 'Decision', gate: '', milestone: 'Milestone', start: 'Start', end: 'End' };
/** The kinds read aloud, a gate's by what it does: for a screen reader, where the mark alone stands on the screen. */
export const BUILDER_KIND_NAMES: Record<BuilderKind, string> = { ...BUILDER_KIND_WORDS, gate: 'Holds back what comes after it' };
/** The start or the end of the process. */
export const isStartOrEnd = (n: Pick<BuilderNode, 'kind'>): boolean => n.kind === 'start' || n.kind === 'end';
/** The keys the two ends are made with (`ends.ts`, where the schedule reads them too). */
import { BUILDER_START, BUILDER_END } from './ends.ts';
export { BUILDER_START, BUILDER_END };

export const BUILDER_WHO = ['nsb', 'sub', 'supplier', 'client', 'outside'] as const;
export type BuilderWho = (typeof BUILDER_WHO)[number];
export const BUILDER_WHO_WORDS: Record<BuilderWho, string> = { nsb: 'NSB', sub: 'Sub', supplier: 'Supplier', client: 'Client', outside: 'Outside' };
/** Who decides a decision. */
export const BUILDER_DECIDERS = ['client', 'nsb'] as const satisfies readonly BuilderWho[];
/**
 * Who approves a gate (design study 09, gates A; Jason, 2026-10-06: "either it is something that happens like a retainer
 * is received and its marked by me on my end as complete or it requires a sign off, which could be something like
 * client, sub, contractor approval"): the client, a sub or NSB; none, and it happens, NSB marking it done.
 */
export const BUILDER_GATE_APPROVERS = ['client', 'sub', 'nsb'] as const satisfies readonly BuilderWho[];
export type GateApprover = (typeof BUILDER_GATE_APPROVERS)[number];
/**
 * Who approves a gate, in words: the client, a sub or NSB. A gate nobody approves happens (a payment in) and has no
 * words: it is its diamond alone (Jason, 2026-10-06: "just don't have anything for gates, maybe just a gate icon").
 */
export function gateOpensWords(who: BuilderWho): string;
export function gateOpensWords(who: BuilderWho | null): string | null;
export function gateOpensWords(who: BuilderWho | null): string | null {
  return who ? `${BUILDER_WHO_WORDS[who]} approves` : null;
}
/** A sub or a supplier brings a trade, picked from the key trades. */
export const bringsTrade = (who: BuilderWho | null): boolean => who === 'sub' || who === 'supplier';

/** The scale a time is picked on, two clicks: the shortest it could take, then the longest (Jason: "1h 2h 4h at the beginning too"). */
export const DURATIONS = [
  { key: '1h', short: '1h', words: '1 hour', days: 1 / 8 },
  { key: '2h', short: '2h', words: '2 hours', days: 1 / 4 },
  { key: '4h', short: '4h', words: '4 hours', days: 1 / 2 },
  { key: '1d', short: '1d', words: '1 day', days: 1 },
  { key: '2d', short: '2d', words: '2 days', days: 2 },
  { key: '3d', short: '3d', words: '3 days', days: 3 },
  { key: '1w', short: '1w', words: '1 week', days: 7 },
  { key: '2w', short: '2w', words: '2 weeks', days: 14 },
  { key: '3w', short: '3w', words: '3 weeks', days: 21 },
  { key: '1m', short: '1m', words: '1 month', days: 30 },
  { key: '2m', short: '2m', words: '2 months', days: 61 },
  { key: '3m', short: '3m', words: '3 months', days: 91 },
  { key: '6m', short: '6m', words: '6 months', days: 183 },
  { key: '1y', short: '1y', words: '1 year', days: 365 },
] as const;
export type DurationKey = (typeof DURATIONS)[number]['key'];
const DURATION_KEYS = DURATIONS.map((d) => d.key) as unknown as [DurationKey, ...DurationKey[]];
const durationAt = (k: DurationKey) => DURATIONS.findIndex((d) => d.key === k);
export const durationOf = (k: DurationKey) => DURATIONS[durationAt(k)]!;

const key = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_.#-]*$/, 'A key is lower case letters, digits and - _ . #')
  .max(120);
const optionId = z.string().regex(/^[a-z0-9]{1,12}$/);
/** What a node depends on: another node's key, or a decision's key and one of its options (`key:option`), when it follows only that option. */
const need = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_.#-]*(:[a-z0-9]{1,12})?$/)
  .max(140);

export const BuilderCommentSchema = z.object({
  id: z.string().min(1).max(60),
  by: z.string().max(120),
  at: z.string().max(40),
  text: z.string().trim().min(1).max(4000),
});
export type BuilderComment = z.infer<typeof BuilderCommentSchema>;

export const BuilderTimeSchema = z
  .object({ low: z.enum(DURATION_KEYS), high: z.enum(DURATION_KEYS) })
  .refine((t) => durationAt(t.low) <= durationAt(t.high), 'The shortest comes first.');
export type BuilderTime = z.infer<typeof BuilderTimeSchema>;

export const BuilderOptionSchema = z.object({ id: optionId, text: z.string().trim().min(1).max(200) });
export type BuilderOption = z.infer<typeof BuilderOptionSchema>;

const BUILDER_SOURCES = [...NODE_SOURCES, 'gate'] as const;
export type BuilderSource = (typeof BUILDER_SOURCES)[number];

export const BuilderNodeSchema = z.object({
  key,
  title: z.string().trim().min(1).max(200),
  kind: z.enum(ALL_KINDS),
  /** Who does a task, or decides a decision; a sign-off is signed by NSB and the client both, a milestone and the ends by nobody. */
  who: z.enum(BUILDER_WHO).nullable(),
  /** A sub's or a supplier's trade, a key from the trade list. */
  trade: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_.-]*$/)
    .max(80)
    .nullable(),
  phase: z.string().max(60).nullable(),
  /**
   * The phases it could go in, while that is still to choose (Jason, 2026-10-06: "make the ambiguous ones part of the
   * configuration questions"): set where a step falls between the sign-offs that close two phases or more, its `phase`
   * the earliest of them meanwhile; gone once a phase is chosen.
   */
  phaseOptions: z.array(z.string().max(60)).max(12).optional(),
  /** How long it takes, or takes to decide: a range on the scale; blank until said. */
  time: BuilderTimeSchema.nullable(),
  /** What it depends on: it cannot start until these are done (a decision's option: and that option is chosen). */
  dependsOn: z.array(need).max(300),
  /** The step whose list it is a task in, if any. */
  parent: key.nullable(),
  /** Where it sits on the board; null while it is only in the list. */
  at: z.object({ x: z.number().finite(), y: z.number().finite() }).nullable(),
  /** What it is, in a sentence or two: NSB's. */
  note: z.string().max(4000).nullable(),
  /**
   * What the client reads when they open it (Jason, 2026-10-06: the note above is NSB's, and Configure asks each step
   * what the client reads): unset until it is asked; empty when the client sees its tasks only. A draft from before has none.
   */
  clientNote: z.string().max(2000).nullable().optional(),
  /**
   * What its painted picture shows, in a few words, when NSB says (Jason, 2026-10-08, design study 24: "A roll of
   * protective paper next to a roll of Japanese Kamoi tape"): painted as it is set, in place of Claude's choice
   * (`domain/step-icons.ts`), with Paint again on its card. Unset, Claude chooses. The picture's alone: nothing of a
   * job reads it, so saving the master for projects carries nothing of it.
   */
  picture: z.string().trim().min(1).max(200).nullable().optional(),
  /**
   * Held by the permit: it cannot start until Permits in hand is passed (Jason, 2026-10-05: "require permit" as a
   * switch on the step, as the long-lead flag holds an order to its gate). A draft from before has neither switch.
   */
  permit: z.boolean().optional(),
  /** The authority inspects it: it is not done until the inspector passes it; a fail is rework on the same step. */
  inspection: z.boolean().optional(),
  /**
   * Checked on site before it goes in: it cannot start until a pre-install inspection finds the site ready for it,
   * measured and the surfaces right (Jason, 2026-10-06: "a pre install inspection as a dependency for any item
   * installed to a site in the same way we have permit"). NSB's own check, a switch as the permit is.
   */
  preInstall: z.boolean().optional(),
  /**
   * What it delivers (Jason, 2026-10-06: "when certain nodes are enabled for a project they add design iterations"):
   * a design review, opened as a draft iteration on the job's Design reviews when the step is added, and finished when
   * the client finishes the review.
   */
  deliversReview: z.boolean().optional(),
  /**
   * It delivers an estimate (design study 17; Jason, 2026-10-07: "an estimate deliverable is all that can really add a
   * new band instance on the funnel"): on a job, NSB sets the job's price on it, a low and a high with a note. Work
   * or a gate; Discovery by default.
   */
  deliversEstimate: z.boolean().optional(),
  /**
   * The special tasks it carries (spec "Pattern Mapping" §3; Jason, 2026-10-15: "a general tool for selecting from among
   * a set of available custom tasks"), from the library (`specials.ts`), only those its kind may carry
   * (`builderAllows`). Omitted when it carries none; a draft from before has none.
   */
  special: z.array(z.enum(SPECIAL_KEYS)).max(SPECIAL_KEYS.length).optional(),
  /** A decision's options: on a job, deciding it records which were chosen (design study 22). */
  options: z.array(BuilderOptionSchema).max(20),
  /**
   * Whether one of its options is chosen, or any number of them (design study 22; Jason, 2026-10-07: "either select one
   * of many or many of many"); a draft from before has none, read as one (`chooseOf`).
   */
  choose: z.enum(DECISION_CHOOSE).optional(),
  /** The option the prototypical funnel follows; the first while none is chosen. */
  follow: optionId.nullable(),
  comments: z.array(BuilderCommentSchema).max(1000),
  /** Where it came from: the master tree, made here, or the research. */
  from: z.enum(['tree', 'new', 'research']),
  /** Where it came from in NSB's thinking, for the bar across its top while the tree is reviewed. */
  source: z.enum(BUILDER_SOURCES).nullable(),
  /** Set aside in the bin, with why, for the clean-up (design study 05, 1A); a draft from before it has none. */
  aside: z
    .object({ why: z.string().trim().min(1).max(2000), by: z.string().trim().min(1).max(200), at: z.string().max(40) })
    .nullable()
    .optional(),
});
export type BuilderNode = z.infer<typeof BuilderNodeSchema>;

/** In the bin: off the board and out of the list, the funnel and the counts. */
export const isAside = (n: Pick<BuilderNode, 'aside'>): boolean => Boolean(n.aside);

/**
 * A phase's box on the paper the board was once (2026-10-04 to 2026-10-06): kept as a draft saved it, read nowhere;
 * the board lays itself out now. Drafts are saved whole, so dropping it would lose nothing but would refuse old ones.
 */
const PhaseBoxSchema = z.object({ x: z.number().finite(), y: z.number().finite(), w: z.number().finite().min(1), h: z.number().finite().min(1) });

export const BuilderPhaseSchema = z.object({
  key: z.string().regex(/^[a-z0-9][a-z0-9_-]*$/).max(60),
  name: z.string().trim().min(1).max(80),
  what: z.string().max(500),
  /** The sign-off or milestone that closes it, once Jason says so. */
  closedBy: key.nullable(),
  /** Of the price spread at the start, the share still open once it is closed: the funnel's narrowing. */
  sureAfter: z.number().min(0).max(1),
  /** Its share of the time, a whole percentage, a weight among the phases still ahead (`TreePhaseDef.share`); a draft from before has none. */
  share: z.number().int().min(0).max(100).optional(),
  /** Its box on the paper the board was once; read nowhere (`PhaseBoxSchema`). */
  at: PhaseBoxSchema.nullable(),
});
export type BuilderPhase = z.infer<typeof BuilderPhaseSchema>;

export const BuilderTreeSchema = z
  .object({
    version: z.literal(2),
    /** The price spread at the very start, either way: 0.5 is ±50%. */
    startSpread: z.number().min(0).max(2),
    phases: z.array(BuilderPhaseSchema).min(1).max(20),
    nodes: z.array(BuilderNodeSchema).max(3000),
    /** Two steps answered as neither depending on the other, or as not a real question, so they are never asked again; a draft from before has none. */
    pairs: z
      .array(z.object({ a: key, b: key, answer: z.enum(['independent', 'irrelevant']) }))
      .max(200000)
      .optional(),
  })
  .superRefine((t, ctx) => {
    const problem = builderProblem(t);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });
export type BuilderTree = z.infer<typeof BuilderTreeSchema>;

/** One entry of depends-on: the node needed, and the option of it followed, if any. */
export function parseNeed(entry: string): { key: string; option: string | null } {
  const at = entry.lastIndexOf(':');
  return at < 0 ? { key: entry, option: null } : { key: entry.slice(0, at), option: entry.slice(at + 1) };
}
const needsOf = (n: Pick<BuilderNode, 'dependsOn'>) => n.dependsOn.map((d) => parseNeed(d).key);

/** The first thing wrong with a tree, in words, or null: unknown names, a node waiting on itself, a loop, an end out of place. */
export function builderProblem(t: Pick<BuilderTree, 'phases' | 'nodes'>): string | null {
  const byKey = new Map<string, Pick<BuilderNode, 'key' | 'title' | 'options' | 'kind'>>();
  for (const n of t.nodes) {
    if (byKey.has(n.key)) return `Two nodes have the key ${n.key}.`;
    byKey.set(n.key, n);
  }
  for (const end of BUILDER_ENDS) if (t.nodes.filter((n) => n.kind === end).length > 1) return `The tree has two ${end === 'start' ? 'starts' : 'ends'}.`;
  const phases = new Set(t.phases.map((p) => p.key));
  if (phases.size !== t.phases.length) return 'Two phases have the same key.';
  for (const p of t.phases) if (p.closedBy !== null && !byKey.has(p.closedBy)) return `${p.name} is closed by ${p.closedBy}, which is not a node.`;
  for (const n of t.nodes) {
    if (n.phase !== null && !phases.has(n.phase)) return `${titleCase(n.title)} is in a phase that does not exist (${n.phase}).`;
    if (n.parent !== null && (!byKey.has(n.parent) || n.parent === n.key)) return `${titleCase(n.title)} is a task of a node that is not there.`;
    if (isStartOrEnd(n) && n.parent !== null) return `${titleCase(n.title)} is the ${n.kind} of the process, never a task in a list.`;
    if (n.kind === 'start' && n.dependsOn.length) return `${titleCase(n.title)} is the start: nothing comes before it.`;
    if (new Set(n.options.map((o) => o.id)).size !== n.options.length) return `${titleCase(n.title)} has two options of the same name.`;
    if (n.follow !== null && !n.options.some((o) => o.id === n.follow)) return `${titleCase(n.title)} follows an option it does not have.`;
    const seen = new Set<string>();
    for (const d of n.dependsOn) {
      const { key: k, option } = parseNeed(d);
      if (k === n.key) return `${titleCase(n.title)} depends on itself.`;
      const target = byKey.get(k);
      if (!target) return `${titleCase(n.title)} depends on ${k}, which is not a node.`;
      if (option !== null && !target.options.some((o) => o.id === option)) return `${titleCase(n.title)} follows an option ${titleCase(target.title)} does not have.`;
      if (target.kind === 'end') return `${titleCase(n.title)} depends on ${titleCase(target.title)}, the end: nothing comes after it.`;
      if (seen.has(k)) return `${titleCase(n.title)} depends on ${titleCase(target.title)} twice.`;
      seen.add(k);
    }
  }
  const loop = builderLoop(t.nodes);
  if (loop) return `The order loops: ${loop.join(' → ')}.`;
  // A task is done in its step's phase or an earlier one (design study 31, slice 4), its step the one at the top.
  const at = (k: string | null) => (k === null ? -1 : t.phases.findIndex((p) => p.key === k));
  const all = new Map(t.nodes.map((n) => [n.key, n]));
  for (const n of t.nodes) {
    let top = n;
    for (let up = n.parent !== null ? all.get(n.parent) : undefined; up; up = up.parent !== null ? all.get(up.parent) : undefined) top = up;
    if (top === n || at(n.phase) < 0 || at(top.phase) < 0 || at(n.phase) <= at(top.phase)) continue;
    return `${titleCase(n.title)} is in ${t.phases[at(n.phase)]!.name}, a later phase than ${titleCase(top.title)}. ${TASK_PHASE_RULE}`;
  }
  return null;
}

/** A loop through depends-on, or a task inside itself, as the titles round it; null when there is none. */
export function builderLoop(nodes: readonly Pick<BuilderNode, 'key' | 'title' | 'dependsOn' | 'parent'>[]): string[] | null {
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const state = new Map<string, 1 | 2>();
  const path: string[] = [];
  const visit = (k: string): string[] | null => {
    if (state.get(k) === 2) return null;
    if (state.get(k) === 1) return [...path.slice(path.indexOf(k)), k].map((x) => byKey.get(x)?.title ?? x);
    state.set(k, 1);
    path.push(k);
    const n = byKey.get(k);
    for (const d of n ? [...needsOf(n), ...(n.parent ? [n.parent] : [])] : []) {
      const found = visit(d);
      if (found) return found;
    }
    path.pop();
    state.set(k, 2);
    return null;
  };
  for (const n of nodes) {
    const found = visit(n.key);
    if (found) return found;
  }
  return null;
}

/** Would `from` depending on `to` close a loop? (Only if `to` already depends on `from`, however far back.) */
export function wouldLoopBuilder(t: Pick<BuilderTree, 'nodes'>, from: string, to: string): boolean {
  if (from === to) return true;
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const seen = new Set<string>();
  const stack = [to];
  while (stack.length) {
    const k = stack.pop()!;
    if (k === from) return true;
    if (seen.has(k)) continue;
    seen.add(k);
    const n = byKey.get(k);
    if (n) stack.push(...needsOf(n), ...(n.parent ? [n.parent] : []));
  }
  return false;
}

// --- Time: a range on the scale ----------------------------------------------------------------------------

/** The range between two stops clicked, whichever was first. */
export function timeBetween(a: DurationKey, b: DurationKey): BuilderTime {
  return durationAt(a) <= durationAt(b) ? { low: a, high: b } : { low: b, high: a };
}

/** In words: "3 days to 2 weeks", or "1 week" when exact. */
export function timeWords(t: BuilderTime): string {
  return t.low === t.high ? durationOf(t.low).words : `${durationOf(t.low).words} to ${durationOf(t.high).words}`;
}

// --- From the master tree, the inverse of locking it in -------------------------------------------------

/**
 * Who does a step, from the tree's owner: lock-in's mapping turned round. An
 * owner the builder has no word for (the architect) is left unset, so locking in
 * keeps the tree's own.
 */
const WHO_OF_OWNER: Record<string, BuilderWho> = { nsb: 'nsb', sub: 'sub', vendor: 'supplier', client: 'client', authority: 'outside' };

/**
 * The master tree as the process tree page edits it: the inverse of locking it
 * in (`lockIn`), so an organization with no draft yet opens its own tree as it
 * is, and locking that in again gives back the same tree. Every step and task a
 * node, each step on the board in the tree's order, with its kind (a gate stays
 * a gate), who does it, its trade, its phase, its list, what it is, what the
 * client reads, its switches and where it came from; what comes after each
 * (`before`) turned back into what it depends on; the tree's phases with what
 * closes each, how sure the price is once it is and its share of the time; the start and the end as
 * the two ends. What the board has no place for stays with the tree and comes
 * back with the next lock-in from it: a step's days (its time here is unset),
 * its client line, its notes.
 */
export function builderFromDoc(doc: ProcessTreeDoc): BuilderTree {
  const byKey = new Map(doc.nodes.map((n) => [n.key, n]));
  // NSB's check that the site is ready, which lock-in puts first in the list of a step checked before it goes in: the
  // step's switch says it, and the next lock-in makes it again.
  const derived = (n: ProcessNodeDefT) => n.kind === 'inspection' && n.owner === 'nsb' && n.parent !== undefined && byKey.get(n.parent)?.preInstall === true;
  const steps = doc.nodes.filter((n) => !derived(n));
  const known = new Set(steps.map((n) => n.key));
  const endOf = (n: ProcessNodeDefT): BuilderEnd | null => (n.parent !== undefined ? null : n.key === BUILDER_START ? 'start' : n.key === BUILDER_END ? 'end' : null);
  // A tree from before the lock-in names no phases of its own: the software's five, nothing closing them.
  const phases: BuilderPhase[] = doc.phases.map((p) => ({ ...p, at: null }));
  const inPhases = new Set(phases.map((p) => p.key));
  // What comes after a step, turned back: each step depends on those it comes after. Nothing comes before the start
  // or after the end.
  const needs = new Map<string, string[]>();
  for (const n of steps) {
    if (endOf(n) === 'end') continue;
    for (const k of n.before ?? []) {
      if (!known.has(k) || k === n.key || endOf(byKey.get(k)!) === 'start' || needs.get(k)?.includes(n.key)) continue;
      needs.set(k, [...(needs.get(k) ?? []), n.key]);
    }
  }
  const at = new Map(doc.nodes.map((n, i) => [n.key, i]));
  const nodes = listOrder(steps).map((n): BuilderNode => {
    const end = endOf(n);
    const kind: BuilderKind = end ?? (n.kind === 'decision' || n.kind === 'gate' || n.kind === 'milestone' ? n.kind : 'task');
    const phase = n.phase ?? phaseOfNode(doc, n.key);
    const inPhase = end === null && phase !== null && inPhases.has(phase);
    return {
      key: n.key,
      title: n.title,
      kind,
      who: kind === 'milestone' || end !== null || !n.owner ? null : (WHO_OF_OWNER[n.owner] ?? null),
      trade: n.trade ?? null,
      // The ends sit in no phase on the board; a task sits in its step's, or the earlier one it is done in, as the tree has it.
      phase: inPhase ? phase : null,
      time: null,
      dependsOn: needs.get(n.key) ?? [],
      parent: n.parent ?? null,
      // On the board in the tree's order, which locking in keeps; a task is in its step's list.
      at: n.parent === undefined ? { x: 0, y: at.get(n.key)! } : null,
      note: n.why ?? null,
      clientNote: n.clientNote ?? null,
      ...(n.permit ? { permit: true } : {}),
      ...(n.inspection ? { inspection: true } : {}),
      ...(n.preInstall ? { preInstall: true } : {}),
      ...(n.deliversReview ? { deliversReview: true } : {}),
      ...(n.deliversEstimate ? { deliversEstimate: true } : {}),
      ...(specialsOf(n).length ? { special: specialsOf(n) } : {}),
      // A decision's options and how many are chosen, as saving the master carried them (design study 22).
      options: kind === 'decision' ? (n.options ?? []).map((o) => ({ id: o.id, text: o.text })) : [],
      ...(kind === 'decision' && n.choose ? { choose: n.choose } : {}),
      follow: null,
      comments: [],
      from: 'tree',
      source: n.source ?? null,
    };
  });
  return builderWithEnds({ version: 2, startSpread: 0.5, phases, nodes });
}

/**
 * The order of the builder's list, as far as a locked-in tree shows it: lock-in
 * writes what comes after a step (`before`) in the order the list had those
 * steps, and a step's tasks in the order its list had them. Each of those runs
 * is kept; between them, the tree's own order. (A tree that was not locked in
 * may ask for a loop: what is left goes on in the tree's order.)
 */
function listOrder(nodes: ProcessNodeDefT[]): ProcessNodeDefT[] {
  const index = new Map(nodes.map((n, i) => [n.key, i]));
  const later = nodes.map(() => new Set<number>());
  const waiting = nodes.map(() => 0);
  const run = (keys: string[]) => {
    const is = keys.flatMap((k) => (index.has(k) ? [index.get(k)!] : []));
    for (let j = 1; j < is.length; j++) {
      if (is[j - 1] === is[j] || later[is[j - 1]!]!.has(is[j]!)) continue;
      later[is[j - 1]!]!.add(is[j]!);
      waiting[is[j]!]! += 1;
    }
  };
  for (const n of nodes) run(n.before ?? []);
  for (const n of nodes) run(nodes.filter((c) => c.parent === n.key).map((c) => c.key));
  const out: ProcessNodeDefT[] = [];
  const done = nodes.map(() => false);
  while (out.length < nodes.length) {
    let next = waiting.findIndex((w, i) => w === 0 && !done[i]);
    if (next < 0) next = done.indexOf(false);
    done[next] = true;
    out.push(nodes[next]!);
    for (const i of later[next]!) waiting[i]! -= 1;
  }
  return out;
}

const END_WORDS: Record<BuilderEnd, { key: string; title: string; note: string }> = {
  start: {
    key: BUILDER_START,
    title: 'Project setup',
    note: 'The project is set up in the portal: whose it is, its people, and where its tree begins. Nothing comes before it.',
  },
  end: {
    key: BUILDER_END,
    // Project Complete (Jason, 2026-10-07): the title alone; marking it done still archives the job.
    title: 'Project complete',
    note: 'The project is closed and archived, its record kept as it ended. Nothing comes after it.',
  },
};

/**
 * The draft with its changes discarded (Jason, 2026-10-07: "there should be a way to clear/cancel and undo changes"):
 * the tree every project has (`builderFromDoc`), keeping what the draft holds that is not the tree itself, so nothing
 * but the changes goes: the comments on each step it still has and what its picture shows (the picture's alone), the
 * steps in the bin, and the board's own settings.
 */
export function builderDiscard(draft: BuilderTree, doc: ProcessTreeDoc): BuilderTree {
  const base = builderFromDoc(doc);
  const was = new Map(draft.nodes.map((n) => [n.key, n]));
  const known = new Set(base.nodes.map((n) => n.key));
  const nodes = base.nodes.map((n) => {
    const d = was.get(n.key);
    return { ...n, ...(d?.comments.length ? { comments: d.comments } : {}), ...(d?.picture ? { picture: d.picture } : {}) };
  });
  const bin = draft.nodes.filter((n) => isAside(n) && !known.has(n.key) && (n.parent === null || known.has(n.parent) || was.get(n.parent)?.aside));
  return { ...base, startSpread: draft.startSpread, ...(draft.pairs ? { pairs: draft.pairs } : {}), nodes: [...nodes, ...bin] };
}

/**
 * The tree with its two ends, when it has not got them (a fresh start, or a
 * draft from before they were): the start first on the board and in the list
 * of nodes, the end last. A tree with both is given back as it is.
 */
export function builderWithEnds(t: BuilderTree): BuilderTree {
  const missing = BUILDER_ENDS.filter((end) => !t.nodes.some((n) => n.kind === end));
  if (!missing.length) return t;
  // Where the board's steps are, only for the order locking in reads (`lockIn` sorts by it): the start above them all, the end below.
  const ys = t.nodes.flatMap((n) => (n.at !== null && n.parent === null ? [n.at.y] : []));
  const top = ys.length ? Math.min(...ys) : 0;
  const bottom = ys.length ? Math.max(...ys) : 0;
  const taken = new Set(t.nodes.map((n) => n.key));
  const make = (end: BuilderEnd, at: { x: number; y: number }): BuilderNode => {
    const w = END_WORDS[end];
    return {
      key: taken.has(w.key) ? builderKey(t, w.title) : w.key,
      title: w.title,
      kind: end,
      who: null,
      trade: null,
      phase: null,
      time: null,
      dependsOn: [],
      parent: null,
      at,
      note: w.note,
      options: [],
      follow: null,
      comments: [],
      from: 'new',
      source: null,
    };
  };
  return {
    ...t,
    nodes: [
      ...(missing.includes('start') ? [make('start', { x: 0, y: top - 1 })] : []),
      ...t.nodes,
      ...(missing.includes('end') ? [make('end', { x: 0, y: bottom + 1 })] : []),
    ],
  };
}

/** Why nothing waits on the end, in words: on the master and on a job alike (core `jobLinkRefusal`, the API's `linkSteps`). */
export const endRefusal = (title: string): string => `${titleCase(title)} is the end: nothing comes after it.`;

/** Why `from` cannot depend on `to` because of the ends, in words, or null: nothing before the start, nothing after the end. */
export function builderEndsRefuse(t: Pick<BuilderTree, 'nodes'>, from: string, to: string): string | null {
  const a = t.nodes.find((n) => n.key === from);
  const b = t.nodes.find((n) => n.key === to);
  if (a?.kind === 'start') return `${titleCase(a.title)} is the start: nothing comes before it.`;
  if (b?.kind === 'end') return endRefusal(b.title);
  return null;
}

// --- How complete a node is ----------------------------------------------------------------------------------

export interface BuilderDetail {
  key: 'who' | 'trade' | 'time' | 'options' | 'note' | 'clientNote' | 'phase';
  words: string;
  done: boolean;
}

/**
 * What a node of its kind needs filled in (Jason, 2026-10-04: "a completeness bar
 * at the bottom of each node to indicate how many details are specified"):
 * who does a task or decides a decision, a sub's or supplier's trade, how long,
 * a decision's options or tasks, what it is, what the client reads, and its
 * phase. A sign-off is signed by NSB and the client both, so it is not asked
 * who; a milestone is nobody's and takes no time.
 */
export function builderDetails(t: Pick<BuilderTree, 'nodes'>, n: BuilderNode): BuilderDetail[] {
  // The ends are nobody's, take no time and sit in no phase: what they are is all there is.
  if (isStartOrEnd(n)) return [{ key: 'note', words: 'what it is', done: Boolean(n.note?.trim()) }];
  const out: BuilderDetail[] = [];
  const doing = n.kind === 'task' || n.kind === 'decision';
  if (doing) out.push({ key: 'who', words: n.kind === 'decision' ? 'who decides' : 'who does it', done: n.who !== null });
  if (n.kind === 'task' && bringsTrade(n.who)) out.push({ key: 'trade', words: 'its trade', done: n.trade !== null });
  // How long is a job's, never the tree's to complete (Jason, 2026-10-06: "node configuration completeness should not
  // depend on a time estimate as it will vary too much between projects"): a step may say it, and need not.
  if (n.kind === 'decision') out.push({ key: 'options', words: 'its options or tasks', done: n.options.length > 0 || t.nodes.some((x) => x.parent === n.key) });
  out.push({ key: 'note', words: n.kind === 'gate' ? 'what it holds back' : 'what it is', done: Boolean(n.note?.trim()) });
  // Set once it is asked, empty when the client is to see its tasks only (an answer too).
  out.push({ key: 'clientNote', words: 'what the client reads', done: typeof n.clientNote === 'string' });
  // A phase still to choose among several is not filled in yet, whatever the step sits in meanwhile.
  out.push({ key: 'phase', words: 'its phase', done: n.phase !== null && (n.phaseOptions?.length ?? 0) < 2 });
  return out;
}

/** How complete a node is: its details filled in, and what is missing; configured when nothing is. */
export function builderCompleteness(t: Pick<BuilderTree, 'nodes'>, n: BuilderNode): { done: number; of: number; missing: string[]; configured: boolean } {
  const d = builderDetails(t, n);
  const done = d.filter((x) => x.done).length;
  return { done, of: d.length, missing: d.filter((x) => !x.done).map((x) => x.words), configured: done === d.length };
}

/** The whole tree's configuration (Jason: "an indicator bar as to the configuration state of the whole tree"): its steps configured, and its details filled in. */
/**
 * The steps on the board with nothing coming into them: no line from another
 * step on the board (Jason, 2026-10-06: "just for our work, highlight nodes in
 * red that don't have anything coming into them"). The start has nothing
 * before it by design, so it is never one.
 */
export function builderLoose(t: Pick<BuilderTree, 'nodes'>): Set<string> {
  const placed = new Set(t.nodes.filter((n) => n.at !== null && n.parent === null && !isAside(n)).map((n) => n.key));
  return new Set(t.nodes.filter((n) => placed.has(n.key) && n.kind !== 'start' && !n.dependsOn.some((d) => placed.has(parseNeed(d).key))).map((n) => n.key));
}

/**
 * The steps on the board that lead to nothing: no step on the board waits on
 * them (Jason, 2026-10-06: "highlight in a different super bright color nodes
 * that don't lead to anything"). The end has nothing after it by design, so it
 * is never one.
 */
export function builderDeadEnds(t: Pick<BuilderTree, 'nodes'>): Set<string> {
  const placed = t.nodes.filter((n) => n.at !== null && n.parent === null && !isAside(n));
  const led = new Set(placed.flatMap((n) => n.dependsOn.map((d) => parseNeed(d).key)));
  return new Set(placed.filter((n) => n.kind !== 'end' && !led.has(n.key)).map((n) => n.key));
}

/** A job run through the tree: what it reaches from the start, what it cannot, and the steps with nothing before them. */
export interface BuilderJobRun {
  reached: Set<string>;
  /** Steps the job has that it cannot reach from the start. */
  held: string[];
  /** Steps the job has with nothing before them in the job: the gap a job of that kind shows in the tree. */
  loose: string[];
  /** It reaches the end. */
  ends: boolean;
}

/**
 * A job run through the tree (Jason, 2026-10-06): a step comes after what it
 * depends on *where the job has it*. A kind of job is simply the steps it has:
 * a wait on a step it does not have is passed over, and nothing a step depends
 * on is brought in with it. The start and the end are in every job.
 */
export function builderJob(t: Pick<BuilderTree, 'nodes'>, has: (key: string) => boolean): BuilderJobRun {
  const steps = t.nodes.filter((n) => n.parent === null && !isAside(n) && (isStartOrEnd(n) || has(n.key)));
  const inJob = new Set(steps.map((n) => n.key));
  const waits = (n: BuilderNode) => n.dependsOn.map((d) => parseNeed(d).key).filter((k) => inJob.has(k));
  const start = steps.find((n) => n.kind === 'start');
  const end = steps.find((n) => n.kind === 'end');
  const reached = new Set<string>(start ? [start.key] : []);
  for (let grew = true; grew; ) {
    grew = false;
    for (const n of steps) {
      if (reached.has(n.key) || n.kind === 'start' || !waits(n).every((k) => reached.has(k))) continue;
      reached.add(n.key);
      grew = true;
    }
  }
  return {
    reached,
    held: steps.filter((n) => !reached.has(n.key)).map((n) => n.key),
    loose: steps.filter((n) => n.kind !== 'start' && waits(n).length === 0).map((n) => n.key),
    ends: end !== undefined && reached.has(end.key),
  };
}

export function builderTreeCompleteness(t: Pick<BuilderTree, 'nodes'>): { configured: number; nodes: number; done: number; of: number } {
  let configured = 0;
  let done = 0;
  let of = 0;
  const steps = t.nodes.filter((n) => n.parent === null && !isStartOrEnd(n) && !isAside(n));
  for (const n of steps) {
    const s = builderCompleteness(t, n);
    if (s.configured) configured += 1;
    done += s.done;
    of += s.of;
  }
  return { configured, nodes: steps.length, done, of };
}

// --- What the board makes: the plan and the prototypical funnel -----------------------------------------

/** The nodes on the board, and the tasks of those nodes (they come with their step). */
export function onBoard(t: Pick<BuilderTree, 'nodes'>): Set<string> {
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const on = new Set(t.nodes.filter((n) => n.at !== null).map((n) => n.key));
  for (let grew = true; grew; ) {
    grew = false;
    for (const n of t.nodes) {
      if (on.has(n.key) || n.parent === null) continue;
      const seen = new Set<string>();
      for (let p: string | null = n.parent; p !== null && !seen.has(p); p = byKey.get(p)?.parent ?? null) {
        seen.add(p);
        if (on.has(p)) {
          on.add(n.key);
          grew = true;
          break;
        }
      }
    }
  }
  return on;
}

/** The option of a decision the funnel follows: the one chosen, else the first. */
export const followedOption = (n: Pick<BuilderNode, 'options' | 'follow'>): string | null => n.follow ?? n.options[0]?.id ?? null;

/**
 * Off the way the funnel follows: what hangs from an option of a decision
 * other than the one followed, what depends on such a node, and their tasks.
 * The prototypical job takes one way through each decision.
 */
export function builderOffPath(t: Pick<BuilderTree, 'nodes'>): Set<string> {
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const off = new Set<string>();
  for (let grew = true; grew; ) {
    grew = false;
    for (const n of t.nodes) {
      // The end comes after whichever way the job went.
      if (off.has(n.key) || n.kind === 'end') continue;
      const leaves =
        (n.parent !== null && off.has(n.parent)) ||
        n.dependsOn.some((d) => {
          const { key: k, option } = parseNeed(d);
          const target = byKey.get(k);
          return off.has(k) || (option !== null && target !== undefined && followedOption(target) !== option);
        });
      if (leaves) {
        off.add(n.key);
        grew = true;
      }
    }
  }
  return off;
}

const daysOf = (n: BuilderNode, which: 'low' | 'high'): number | null => (n.time ? Math.max(1, Math.round(durationOf(n.time[which]).days)) : null);

/**
 * The board as a job, along the way the funnel follows: every step on it, in
 * the order of what depends on what, each its shortest or longest. A step's
 * time covers its tasks, which are one performer's sequence inside it, words in
 * its list and never planned on their own; what a task waits on, its step waits on.
 */
export function builderProcess(t: Pick<BuilderTree, 'nodes'>, which: 'low' | 'high' = 'high'): ProjectProcess {
  const on = onBoard(t);
  const off = builderOffPath(t);
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const stepOf = (k: string): string => {
    let n = byKey.get(k);
    for (let i = 0; n && n.parent !== null && i < 100; i++) n = byKey.get(n.parent);
    return n?.key ?? k;
  };
  const live = t.nodes.filter((n) => n.parent === null && on.has(n.key) && !off.has(n.key));
  const here = new Set(live.map((n) => n.key));
  const steps: ProcessStep[] = live.map((n) => ({
    id: n.key,
    key: null,
    instance: null,
    kind: n.kind === 'task' ? (n.who === 'outside' ? 'inspection' : 'work') : n.kind === 'start' || n.kind === 'end' ? 'milestone' : n.kind,
    title: n.title,
    parentId: null,
    state: 'not_started',
    days: n.kind === 'task' || n.kind === 'decision' ? daysOf(n, which) : null,
    off: false,
    startedOn: null,
    doneOn: null,
  }));
  const needs = new Map<string, Set<string>>();
  for (const n of t.nodes) {
    const step = stepOf(n.key);
    if (!here.has(step)) continue;
    for (const d of needsOf(n)) {
      const s = stepOf(d);
      if (s !== step && here.has(s)) needs.set(step, (needs.get(step) ?? new Set()).add(s));
    }
  }
  const links = [...needs].flatMap(([stepId, ds]) => [...ds].map((needsId) => ({ stepId, needsId, kind: 'requires' as const })));
  return { kind: 'client', steps, links };
}

export interface PrototypePhase {
  key: string;
  name: string;
  /** Days from the start of the job, each node at its longest. */
  start: number;
  end: number;
  /** The same, each node at its shortest. */
  startLow: number;
  endLow: number;
  /** The spread either way once it is closed: 0.25 is ±25%. */
  spreadAfter: number;
  /** The node that closes it, when it is on the board. */
  closedBy: string | null;
  /** How many nodes on the board it holds. */
  count: number;
}

export interface BuilderPlan {
  /** The plan with each node at its longest. */
  schedule: Schedule;
  phases: PrototypePhase[];
  /** Days from the start to the end of the last phase, each node at its longest, and at its shortest. */
  days: number;
  daysLow: number;
  startSpread: number;
  /** Steps planned; a step's tasks go with it, words in its list. */
  count: number;
  /** Steps of work and decisions planned with no time said: each counted as a week. */
  untimed: number;
  /** Steps on the board off the way the funnel follows. */
  offPath: number;
}

const DAY = 86_400_000;
const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY);

/**
 * The prototypical funnel from the board (Jason, 2026-10-04: "at the top of
 * the process tree design page i want to see the prototypical funnel build in
 * real time as I define the tree and the dependencies"): the board planned as
 * one job from `today`, along one way through each decision, twice, each node
 * at its shortest and at its longest; each phase ending when what closes it is
 * reached (or its last node ends, when nothing on the board closes it), never
 * before the phase before it; and the price spread narrowing to each phase's
 * share as it closes, never widening again.
 */
export function builderPlan(t: BuilderTree, today: string): BuilderPlan {
  const high = builderProcess(t, 'high');
  const low = builderProcess(t, 'low');
  const sHigh = schedule(high, today);
  const sLow = schedule(low, today);
  const phaseOf = new Map(t.nodes.map((n) => [n.key, n.phase]));
  const finish = (s: Schedule, k: string) => {
    const tm = s.timing.get(k);
    return tm ? daysBetween(today, tm.finish) : null;
  };
  const ends = (s: Schedule, steps: ProcessStep[], closer: string | null) =>
    closer ? [finish(s, closer)!] : steps.map((x) => finish(s, x.id)).filter((d): d is number => d !== null);
  let before = 0;
  let beforeLow = 0;
  let spread = t.startSpread;
  const phases: PrototypePhase[] = t.phases.map((ph) => {
    const members = high.steps.filter((s) => phaseOf.get(s.id) === ph.key);
    const closer = ph.closedBy !== null && sHigh.timing.has(ph.closedBy) ? ph.closedBy : null;
    const end = Math.max(before, ...ends(sHigh, members, closer));
    const endLow = Math.max(beforeLow, ...ends(sLow, members, closer));
    spread = Math.min(spread, t.startSpread * ph.sureAfter);
    const out = { key: ph.key, name: ph.name, start: before, end, startLow: beforeLow, endLow, spreadAfter: spread, closedBy: closer, count: members.length };
    before = end;
    beforeLow = endLow;
    return out;
  });
  const byKey = new Map(t.nodes.map((n) => [n.key, n]));
  const untimed = high.steps.filter((s) => {
    const n = byKey.get(s.id);
    return n !== undefined && (n.kind === 'task' || n.kind === 'decision') && n.time === null;
  }).length;
  const on = onBoard(t);
  const offPath = [...builderOffPath(t)].filter((k) => on.has(k) && byKey.get(k)?.parent === null).length;
  const stepCount = high.steps.filter((s) => !isStartOrEnd(byKey.get(s.id) ?? { kind: 'task' })).length;
  // To the end of everything planned, whether or not it is in a phase yet.
  const last = (s: Schedule) => (s.end ? daysBetween(today, s.end.on) : 0);
  return {
    schedule: sHigh,
    phases,
    days: Math.max(before, last(sHigh)),
    daysLow: Math.max(beforeLow, last(sLow)),
    startSpread: t.startSpread,
    count: stepCount,
    untimed,
    offPath,
  };
}

const slugOf = (words: string, fallback: string) =>
  words
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
    .replace(/-+$/g, '') || fallback;

/** A key for a new node from its title: a slug, unique in the tree. */
export function builderKey(t: Pick<BuilderTree, 'nodes'>, title: string): string {
  const base = slugOf(title, 'node');
  const taken = new Set(t.nodes.map((n) => n.key));
  let k = `new.${base}`;
  for (let i = 2; taken.has(k); i++) k = `new.${base}-${i}`;
  return k;
}

// --- Edits: each gives the tree changed, never changing the one it was given ---------------------------

const withNode = (t: BuilderTree, key: string, f: (n: BuilderNode) => BuilderNode): BuilderTree => ({ ...t, nodes: t.nodes.map((n) => (n.key === key ? f(n) : n)) });

/**
 * On the board (Put them on the board, for a step left in the old list): the board lays itself out, so `at` is only
 * its place in the order locking in reads. A task taken out onto the board is a step of its own.
 */
export function builderPlace(t: BuilderTree, key: string, at: { x: number; y: number }): BuilderTree {
  return withNode(t, key, (n) => ({ ...n, at: { x: Math.round(at.x), y: Math.round(at.y) }, parent: null }));
}

/** `from` depends on `to`, or on one of `to`'s options: it follows only that option. One link between two nodes at most; never before the start or after the end. */
export function builderLink(t: BuilderTree, from: string, to: string, option: string | null = null): BuilderTree {
  if (builderEndsRefuse(t, from, to)) return t;
  return withNode(t, from, (n) => ({ ...n, dependsOn: [...n.dependsOn.filter((d) => parseNeed(d).key !== to), option ? `${to}:${option}` : to] }));
}

/**
 * A step put in a phase, its tasks with it (Jason, 2026-10-05: "just show each
 * node and I select a phase"; a step dragged into another phase's block, or
 * its phase chosen on its card). Not the start or the end, and only a phase
 * the tree has. A task done in an earlier phase than the step (design study
 * 31, slice 4) keeps its phase, brought to the step's new one only where it
 * would be later than it. A task put in a later phase than its step is put
 * there, and refused by `builderProblem` (`TASK_PHASE_RULE`).
 */
export function builderSetPhase(t: BuilderTree, key: string, phase: string | null): BuilderTree {
  const n = t.nodes.find((x) => x.key === key);
  if (!n || isStartOrEnd(n) || (phase !== null && !t.phases.some((p) => p.key === phase))) return t;
  const inside = new Set<string>();
  for (let grew = true; grew; ) {
    grew = false;
    for (const x of t.nodes) {
      if (x.parent !== null && (x.parent === key || inside.has(x.parent)) && !inside.has(x.key)) {
        inside.add(x.key);
        grew = true;
      }
    }
  }
  const at = (k: string | null) => (k === null ? -1 : t.phases.findIndex((p) => p.key === k));
  const was = at(n.phase);
  /** A task's phase as the step moves: with it, but where it is done early, and still is. */
  const taskPhase = (x: BuilderNode): string | null => {
    const own = at(x.phase);
    if (own < 0 || was < 0 || own >= was) return phase;
    return phase !== null && own > at(phase) ? phase : x.phase;
  };
  // Choosing its phase answers the question of which: what it could go in is no longer asked.
  return {
    ...t,
    nodes: t.nodes.map((x) => (x.key === key ? { ...withoutPhaseOptions(x), phase } : inside.has(x.key) ? { ...x, phase: taskPhase(x) } : x)),
  };
}

/** A node with no phase still to choose. */
const withoutPhaseOptions = (n: BuilderNode): BuilderNode => {
  if (n.phaseOptions === undefined) return n;
  const { phaseOptions: _done, ...rest } = n;
  return rest;
};

/** `from` no longer depends on `to`, on any option of it. */
export function builderUnlink(t: BuilderTree, from: string, to: string): BuilderTree {
  return withNode(t, from, (n) => ({ ...n, dependsOn: n.dependsOn.filter((d) => parseNeed(d).key !== to) }));
}

/** A new node, made here: a task unless said otherwise, nothing said about it yet, in the list until it is placed. */
export function builderAdd(
  t: BuilderTree,
  fields: { title: string; kind?: BuilderKind; phase?: string | null; at?: { x: number; y: number } | null; parent?: string | null },
): { tree: BuilderTree; key: string } {
  const key = builderKey(t, fields.title);
  const node: BuilderNode = {
    key,
    title: fields.title.trim(),
    // There is one start and one end: a node made here is never either.
    kind: fields.kind && !isStartOrEnd({ kind: fields.kind }) ? fields.kind : 'task',
    who: null,
    trade: null,
    phase: fields.phase ?? null,
    time: null,
    dependsOn: [],
    parent: fields.parent ?? null,
    at: fields.at ? { x: Math.round(fields.at.x), y: Math.round(fields.at.y) } : null,
    note: null,
    options: [],
    follow: null,
    comments: [],
    from: 'new',
    source: null,
  };
  const tree = { ...t, nodes: [...t.nodes, node] };
  return { tree, key };
}

/**
 * Out of the tree: every link to it goes with it, its tasks become steps of
 * their own (in the list), and a phase it closed closes when its last node ends.
 */
export function builderRemove(t: BuilderTree, key: string): BuilderTree {
  if (t.nodes.some((n) => n.key === key && isStartOrEnd(n))) return t;
  return {
    ...t,
    phases: t.phases.map((p) => (p.closedBy === key ? { ...p, closedBy: null } : p)),
    nodes: t.nodes
      .filter((n) => n.key !== key)
      .map((n) => {
        const dependsOn = n.dependsOn.filter((d) => parseNeed(d).key !== key);
        return dependsOn.length !== n.dependsOn.length || n.parent === key ? { ...n, dependsOn, parent: n.parent === key ? null : n.parent } : n;
      }),
  };
}

export type BuilderNodePatch = Partial<Pick<BuilderNode, 'title' | 'kind' | 'who' | 'trade' | 'phase' | 'time' | 'note' | 'clientNote' | 'picture' | 'permit' | 'inspection' | 'preInstall' | 'deliversReview' | 'deliversEstimate' | 'special' | 'choose'>>;

/**
 * Said of a node. A milestone is nobody's to do: making one clears who; a gate
 * keeps who approves it (the client, a sub or NSB), none when it happens. The
 * ends keep what they are: only their title and what they are change, and no
 * node is made one.
 */
export function builderUpdate(t: BuilderTree, key: string, patch: BuilderNodePatch): BuilderTree {
  return withNode(t, key, (n) => {
    if (isStartOrEnd(n)) return { ...n, title: patch.title ?? n.title, note: patch.note !== undefined ? patch.note : n.note, ...(patch.picture !== undefined ? { picture: patch.picture } : {}) };
    const merged = { ...n, ...patch };
    if (isStartOrEnd(merged)) return n;
    // One step of the tree carries a special task: one another step carries already is not taken on (`builderSpecialRefuse`).
    const taken = specialsOf(patch).filter((k) => !specialsOf(n).includes(k) && builderSpecialRefuse(t, key, k) !== null);
    // Only the special tasks it may carry: made another kind or an inspector's, it loses the rest; none, and no list.
    const next = withAllowedSpecials(taken.length ? { ...merged, special: specialsOf(merged).filter((k) => !taken.includes(k)) } : merged);
    // A milestone is nobody's; a gate is approved by the client, a sub or NSB, or happens (who none). A step made a gate
    // starts as happening: who did the work is not who approves it.
    if (next.kind === 'milestone') return { ...next, who: null };
    if (next.kind === 'gate' && n.kind !== 'gate' && patch.who === undefined) return { ...next, who: null };
    if (next.kind === 'gate' && next.who !== null && !(BUILDER_GATE_APPROVERS as readonly string[]).includes(next.who)) return { ...next, who: null };
    return next;
  });
}

/** What a kind of the builder's is in the tree: a task is work; the ends are neither, and carry nothing. */
const NODE_KIND_OF: Partial<Record<BuilderKind, NodeKind>> = { task: 'work', decision: 'decision', gate: 'gate', milestone: 'milestone' };

/**
 * Whether a node may carry the special task (spec "Pattern Mapping" §3.3): a kind the library allows, in the builder's
 * words, at the top (a task in a step's list has no settings of its own on a job), and never an inspector's, which
 * locking in makes an inspection.
 */
export function builderAllows(n: { kind: BuilderKind; parent: string | null; who?: BuilderWho | null }, key: SpecialKey): boolean {
  const k = NODE_KIND_OF[n.kind];
  return k !== undefined && n.parent === null && n.who !== 'outside' && SPECIAL_TASKS[key].kinds.includes(k);
}

/**
 * Why `key` cannot take on a special task, in words, or null: another step of the tree carries it already, one step
 * carrying each (Pattern mapping is the job's one language, spec "Pattern Mapping" §3.3). One in the bin is not in the
 * tree; lock-in refuses a tree with two, in words (`lockIn`).
 */
export function builderSpecialRefuse(t: Pick<BuilderTree, 'nodes'>, key: string, special: SpecialKey): string | null {
  const other = t.nodes.find((n) => n.key !== key && !isAside(n) && specialsOf(n).includes(special));
  return other ? `“${titleCase(other.title)}” carries ${SPECIAL_TASKS[special].name}: one step of the tree carries it, so take it off there first.` : null;
}

/**
 * What a step's card shows of the special tasks (spec "Pattern Mapping" §3.2; Jason, 2026-10-17: "it should be a
 * generic add and give that as an optional special task, there really is only one pattern mapping step after all"):
 * the ones it carries, and the ones it could take now, its kind allowing them, it lacking them and no other step of the
 * tree carrying them (`builderSpecialRefuse`). Both empty, the card says nothing of special tasks.
 */
export function specialOffers(t: Pick<BuilderTree, 'nodes'>, n: BuilderNode): { carried: SpecialKey[]; addable: SpecialKey[] } {
  const carried = specialsOf(n).filter((k) => builderAllows(n, k));
  const addable = SPECIAL_KEYS.filter((k) => builderAllows(n, k) && !carried.includes(k) && builderSpecialRefuse(t, n.key, k) === null);
  return { carried, addable };
}

/** The node without the special tasks it may no longer carry, and without the list when none are left. */
function withAllowedSpecials(n: BuilderNode): BuilderNode {
  if (!n.special) return n;
  const { special, ...rest } = n;
  const kept = specialsOf({ special }).filter((k) => builderAllows(n, k));
  return kept.length ? { ...rest, special: kept } : rest;
}

/** A task in `parent`'s list, off the board as a box of its own; null makes it a step of its own, in the list until placed. */
export function builderNest(t: BuilderTree, key: string, parent: string | null): BuilderTree {
  // Into a step's list it loses its special tasks: a task in a list carries none (`builderAllows`).
  return withNode(t, key, (n) => (isStartOrEnd(n) ? n : withAllowedSpecials({ ...n, parent, at: parent === null ? n.at : null })));
}

export function builderComment(t: BuilderTree, key: string, c: BuilderComment): BuilderTree {
  return withNode(t, key, (n) => ({ ...n, comments: [...n.comments, c] }));
}

export function builderUncomment(t: BuilderTree, key: string, id: string): BuilderTree {
  return withNode(t, key, (n) => ({ ...n, comments: n.comments.filter((c) => c.id !== id) }));
}

/** A decision's new option, one of what it is decided by choosing (design study 22): never a way on of its own. */
export function builderAddOption(t: BuilderTree, key: string, text: string): { tree: BuilderTree; id: string } {
  const n = t.nodes.find((x) => x.key === key);
  const used = new Set(n?.options.map((o) => o.id) ?? []);
  let i = 1;
  while (used.has(`o${i}`)) i += 1;
  const id = `o${i}`;
  return { tree: withNode(t, key, (x) => ({ ...x, options: [...x.options, { id, text: text.trim() }] })), id };
}

export function builderRenameOption(t: BuilderTree, key: string, id: string, text: string): BuilderTree {
  return withNode(t, key, (n) => ({ ...n, options: n.options.map((o) => (o.id === id ? { ...o, text: text.trim() } : o)) }));
}

/** An option taken out: what followed it still comes after the decision, whatever is decided. */
export function builderRemoveOption(t: BuilderTree, key: string, id: string): BuilderTree {
  const gone = `${key}:${id}`;
  return {
    ...t,
    nodes: t.nodes.map((n) =>
      n.key === key
        ? { ...n, options: n.options.filter((o) => o.id !== id), follow: n.follow === id ? null : n.follow }
        : n.dependsOn.includes(gone)
          ? { ...n, dependsOn: n.dependsOn.map((d) => (d === gone ? key : d)) }
          : n,
    ),
  };
}

/** The option the prototypical funnel follows through a decision. */
export function builderFollow(t: BuilderTree, key: string, id: string): BuilderTree {
  return withNode(t, key, (n) => ({ ...n, follow: id }));
}

// --- The phases: the five by default, which can be renamed, reordered, added to and taken from ---------

export type BuilderPhasePatch = Partial<Pick<BuilderPhase, 'name' | 'what' | 'closedBy' | 'sureAfter' | 'share'>>;

export function builderUpdatePhase(t: BuilderTree, key: string, patch: BuilderPhasePatch): BuilderTree {
  return { ...t, phases: t.phases.map((p) => (p.key === key ? { ...p, ...patch } : p)) };
}

/** A new phase at the end, as sure as the last one and with no share of the time until set. */
export function builderAddPhase(t: BuilderTree, name: string): { tree: BuilderTree; key: string } {
  const base = slugOf(name, 'phase');
  const taken = new Set(t.phases.map((p) => p.key));
  let key = base;
  for (let i = 2; taken.has(key); i++) key = `${base}-${i}`;
  const last = t.phases.at(-1);
  return { tree: { ...t, phases: [...t.phases, { key, name: name.trim(), what: '', closedBy: null, sureAfter: last?.sureAfter ?? 1, share: 0, at: null }] }, key };
}

/** A phase taken out: its nodes are in no phase until put in one. The last phase stays. */
export function builderRemovePhase(t: BuilderTree, key: string): BuilderTree {
  if (t.phases.length <= 1) return t;
  return { ...t, phases: t.phases.filter((p) => p.key !== key), nodes: t.nodes.map((n) => (n.phase === key ? { ...n, phase: null } : n)) };
}

/** A phase one place earlier (-1) or later (1). */
export function builderMovePhase(t: BuilderTree, key: string, by: -1 | 1): BuilderTree {
  const i = t.phases.findIndex((p) => p.key === key);
  const j = i + by;
  if (i < 0 || j < 0 || j >= t.phases.length) return t;
  const phases = [...t.phases];
  [phases[i], phases[j]] = [phases[j]!, phases[i]!];
  return { ...t, phases };
}
